import React from 'react';
import {
  Box,
  Typography,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Container,
  Grid,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Help as HelpIcon,
  Chat as ChatIcon,
  Dashboard as DashboardIcon,
  Settings as SettingsIcon,
  Campaign as CampaignIcon,
  People as PeopleIcon,
  WhatsApp as WhatsAppIcon
} from '@mui/icons-material';

const Help = () => {
  const faqData = [
    {
      question: "Como iniciar um atendimento?",
      answer: "Para iniciar um atendimento, acesse a seção 'Chat' no menu lateral e clique em 'Novo Atendimento' ou aguarde a chegada de novos tickets na fila."
    },
    {
      question: "Como gerenciar filas de atendimento?",
      answer: "Acesse 'Configurações' > 'Filas' para criar, editar ou remover filas de atendimento. Você pode definir horários de funcionamento e atendentes responsáveis."
    },
    {
      question: "Como criar respostas rápidas?",
      answer: "Vá em 'Configurações' > 'Respostas Rápidas' para criar mensagens pré-definidas que podem ser usadas durante os atendimentos para agilizar o processo."
    },
    {
      question: "Como visualizar relatórios?",
      answer: "O Dashboard principal apresenta métricas em tempo real. Para relatórios mais detalhados, acesse a seção específica de cada módulo."
    },
    {
      question: "Como gerenciar contatos?",
      answer: "Na seção 'Contatos', você pode visualizar, editar e organizar todos os contatos do sistema, incluindo histórico de conversas."
    },
    {
      question: "Como configurar campanhas?",
      answer: "Acesse 'Campanhas' para criar e gerenciar campanhas de marketing, definindo público-alvo, mensagens e horários de envio."
    }
  ];

  const features = [
    {
      icon: <ChatIcon color="primary" />,
      title: "Chat em Tempo Real",
      description: "Sistema de chat integrado para atendimento instantâneo aos clientes."
    },
    {
      icon: <DashboardIcon color="primary" />,
      title: "Dashboard Analítico",
      description: "Visualize métricas e estatísticas de atendimento em tempo real."
    },
    {
      icon: <PeopleIcon color="primary" />,
      title: "Gerenciamento de Contatos",
      description: "Organize e gerencie todos os contatos e histórico de conversas."
    },
    {
      icon: <CampaignIcon color="primary" />,
      title: "Campanhas de Marketing",
      description: "Crie e execute campanhas de marketing direcionadas."
    },
    {
      icon: <SettingsIcon color="primary" />,
      title: "Configurações Avançadas",
      description: "Personalize o sistema de acordo com suas necessidades."
    },
    {
      icon: <WhatsAppIcon color="primary" />,
      title: "WhatsApp Oficial/Não Oficial",
      description: "Integração com WhatsApp (Cloud API e Baileys), com fallback e monitoramento."
    }
  ];

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ mb: 4, textAlign: 'center' }}>
        <HelpIcon sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
        <Typography variant="h3" component="h1" gutterBottom>
          Central de Ajuda
        </Typography>
        <Typography variant="h6" color="text.secondary">
          Encontre respostas para suas dúvidas e aprenda a usar todas as funcionalidades do sistema
        </Typography>
      </Box>

      <Grid container spacing={4}>
        {/* FAQ Section */}
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 3, mb: 4 }}>
            <Typography variant="h4" gutterBottom>
              Perguntas Frequentes
            </Typography>
            <Divider sx={{ mb: 3 }} />
            
            {faqData.map((faq, index) => (
              <Accordion key={index} sx={{ mb: 1 }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="h6">{faq.question}</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography color="text.secondary">
                    {faq.answer}
                  </Typography>
                </AccordionDetails>
              </Accordion>
            ))}
          </Paper>
        </Grid>

        {/* Quick Links */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3, mb: 4 }}>
            <Typography variant="h5" gutterBottom>
              Links Rápidos
            </Typography>
            <Divider sx={{ mb: 2 }} />
            
            <List>
              <ListItem button>
                <ListItemIcon>
                  <DashboardIcon color="primary" />
                </ListItemIcon>
                <ListItemText 
                  primary="Dashboard" 
                  secondary="Visão geral do sistema"
                />
              </ListItem>
              
              <ListItem button>
                <ListItemIcon>
                  <ChatIcon color="primary" />
                </ListItemIcon>
                <ListItemText 
                  primary="Atendimentos" 
                  secondary="Gerenciar conversas"
                />
              </ListItem>
              
              <ListItem button>
                <ListItemIcon>
                  <SettingsIcon color="primary" />
                </ListItemIcon>
                <ListItemText 
                  primary="Configurações" 
                  secondary="Personalizar sistema"
                />
              </ListItem>
            </List>
          </Paper>
        </Grid>

        {/* Features Overview */}
        <Grid item xs={12}>
          <Typography variant="h4" gutterBottom sx={{ mb: 3 }}>
            Funcionalidades do Sistema
          </Typography>
          
          <Grid container spacing={3}>
            {features.map((feature, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                  <CardContent sx={{ flexGrow: 1, textAlign: 'center' }}>
                    <Box sx={{ mb: 2 }}>
                      {feature.icon}
                    </Box>
                    <Typography variant="h6" component="h3" gutterBottom>
                      {feature.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {feature.description}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Grid>

        {/* Contact Support */}
        <Grid item xs={12}>
          <Paper sx={{ p: 4, textAlign: 'center', bgcolor: 'primary.main', color: 'white' }}>
            <Typography variant="h5" gutterBottom>
              Precisa de mais ajuda?
            </Typography>
            <Typography variant="body1" sx={{ mb: 2 }}>
              Nossa equipe de suporte está pronta para ajudar você
            </Typography>
            <Typography variant="body2">
              Entre em contato através do sistema de tickets ou consulte a documentação técnica
            </Typography>
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
};

export default Help;
